import React from "react";

import { getBreakdown } from "../../lib/api";
import type { AuthHeaders } from "../../lib/auth";
import { toNumber } from "../../lib/format";
import { getHashParam, setHashParam } from "../../lib/hashState";
import type {
  Breakdown,
  DateRange,
  InsightDimension,
  InsightFilters,
  InsightGroup,
  InsightMetrics,
  SeriesMetric,
} from "../../lib/types";
import {
  Button,
  Card,
  Delta,
  EmptyState,
  Icon,
  IconButton,
  InfoDot,
  Segmented,
  Select,
  Skeleton,
  Sparkline,
  StatStrip,
  StatTile,
  Table,
  Callout,
  CsvButton,
} from "../../design";
import { Floating, listKey, usePopover } from "../../design/popover";
import { MONEY_SAVED_TIP } from "./Savings";
import { Legend, TimeChart } from "./shared";
import { useEntity } from "./Entity";
import {
  CACHE_HIT_TIP,
  DIMENSION_LABELS,
  OTHER,
  drillId,
  filtersOf,
  humanize,
  offlineNote,
  pct,
  previousRange,
  shortRepo,
  usdShort,
  useLoad,
  useStableColors,
  type ActiveFilter,
} from "./util";

// Which filter a clicked row of each dimension narrows to.
const DRILL: Partial<Record<InsightDimension, keyof InsightFilters>> = {
  user: "user",
  team: "team",
  repo: "repo",
  model: "model",
  kind: "kind",
  client: "client",
  tenant: "tenant",
  feature: "feature",
  env: "env",
  key: "key",
  device: "device",
};

const NO_KEY = new Set([null, "__none__"]);

export const SPEND_TIP =
  "Spend at API prices is input price plus output price. Input includes prompt tokens and cache reads and writes. It is not your bill: subscription usage is covered by your plan. Savings percentages use input price before compression: input price plus money saved. Output price is excluded from that percentage.";
const BILLED_TIP =
  "Input and output usage on your own provider API keys, valued at public API prices. Input includes cache reads and writes.";
const PLAN_TIP =
  "Input and output usage on a Claude or ChatGPT plan, valued at API prices. The plan's flat fee already pays for this usage.";

const METRIC_LABEL: Record<SeriesMetric, string> = {
  cost_usd: "Total price",
  input_cost_usd: "Input price",
  output_cost_usd: "Output price",
  total_cost_usd: "Total price",
  saved_usd: "Saved",
  requests: "Requests",
  tokens: "Tokens",
  sessions: "Sessions",
  errors: "Failed requests",
};

function tokensOf(m: InsightMetrics): number {
  return m.input_tokens + m.output_tokens + m.cache_read + m.cache_write;
}

function valueOf(m: InsightMetrics, metric: SeriesMetric): number {
  if (metric === "tokens") return tokensOf(m);
  return toNumber(m[metric]);
}

function fmt(metric: SeriesMetric, v: number): string {
  return metric.endsWith("_usd") ? usdShort(v) : humanize(Math.round(v));
}

export function FilterBar({
  filters,
  onRemove,
}: {
  filters: ActiveFilter[];
  onRemove: (key: keyof InsightFilters) => void;
}) {
  if (filters.length === 0) return null;
  return (
    <div className="ana-chips">
      {filters.map((f) => (
        <span key={f.key} className="ana-filter">
          <span className="ana-filter-text" title={f.label}>
            {f.label}
          </span>
          <IconButton
            icon="x"
            size="sm"
            label={`Remove ${f.label} filter`}
            onClick={() => onRemove(f.key)}
          />
        </span>
      ))}
    </div>
  );
}

/** "Filter": pick a dimension, then one of its values in this range. */
export function FilterPicker({
  authHeaders,
  base,
  range,
  dimensions,
  filters,
  onPick,
}: {
  authHeaders: AuthHeaders;
  base: string;
  range: DateRange;
  dimensions: InsightDimension[];
  filters: ActiveFilter[];
  onPick: (f: ActiveFilter) => void;
}) {
  const pop = usePopover<HTMLButtonElement>("end");
  const usable = dimensions.filter(
    (d) => DRILL[d] && !filters.some((f) => f.key === DRILL[d]),
  );
  const [dim, setDim] = React.useState<InsightDimension | null>(null);
  const flt = filtersOf(filters);
  const values = useLoad(
    () =>
      dim && pop.open
        ? getBreakdown(authHeaders, base, range, dim, flt).then((b) =>
            b.groups.filter((g) => !NO_KEY.has(g.key)),
          )
        : Promise.resolve([]),
    [pop.open, dim, base, range.start, range.end, JSON.stringify(flt)],
  ).data;
  const menu = React.useRef<HTMLDivElement | null>(null);
  const typed = React.useRef({ text: "", at: 0 });
  const items = () =>
    Array.from(
      menu.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [],
    );
  React.useEffect(() => {
    if (!pop.open) {
      setDim(null);
      return;
    }
    requestAnimationFrame(() => items()[0]?.focus());
  }, [pop.open, dim, values]);
  const onMenuKey = (e: React.KeyboardEvent) => {
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLElement);
    if (e.key === "ArrowLeft" && dim != null) {
      e.preventDefault();
      setDim(null);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      pop.close();
    } else if (e.key === "ArrowRight" && dim == null && at >= 0) {
      e.preventDefault();
      list[at].click();
    } else if (e.key === "Tab") {
      pop.close(false);
    } else {
      const next = listKey(
        e.key,
        Math.max(at, 0),
        list.map((el) => el.textContent ?? ""),
        typed,
      );
      if (next != null) {
        e.preventDefault();
        list[next]?.focus();
      }
    }
  };
  if (usable.length === 0) return null;
  return (
    <div ref={pop.root} className="ana-filterpick">
      <Button
        ref={pop.trigger}
        icon="plus"
        aria-haspopup="menu"
        aria-expanded={pop.open}
        onClick={pop.toggle}
      >
        Filter
      </Button>
      {pop.open && (
        <Floating>
          <div
            ref={(el) => {
              menu.current = el;
              pop.panel.current = el;
            }}
            style={pop.style}
            className="ds-pop menu ana-filterpop"
            role="menu"
            aria-label={dim ? DIMENSION_LABELS[dim] : "Filter by"}
            onKeyDown={onMenuKey}
          >
            {dim != null && (
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                className="ds-pop-item ana-pop-back"
                onClick={() => setDim(null)}
              >
                <Icon name="chevron-left" size={14} />
                <span className="ds-pop-text">{DIMENSION_LABELS[dim]}</span>
              </button>
            )}
            {dim == null
              ? usable.map((d) => (
                  <button
                    key={d}
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className="ds-pop-item"
                    onClick={() => setDim(d)}
                  >
                    <span className="ds-pop-text">{DIMENSION_LABELS[d]}</span>
                    <Icon name="chevron-right" size={14} />
                  </button>
                ))
              : (values ?? []).slice(0, 30).map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className="ds-pop-item"
                    onClick={() => {
                      const key = DRILL[dim];
                      if (key)
                        onPick({
                          key,
                          value: g.key as string,
                          label: `${DIMENSION_LABELS[dim]}: ${g.label}`,
                        });
                      pop.close();
                    }}
                  >
                    <span className="ds-pop-text ana-ellipsis" title={g.label}>
                      {dim === "repo" ? shortRepo(g.label) : g.label}
                    </span>
                  </button>
                ))}
            {dim != null && values && values.length === 0 && (
              <div className="ana-pop-empty">Nothing in this range</div>
            )}
          </div>
        </Floating>
      )}
    </div>
  );
}

export function SpendExplorer({
  authHeaders,
  base,
  range,
  dimensions,
  filters,
  onDrill,
  peopleLabel = "People",
  title = "Portfolio",
  bare = false,
  initialDim,
  onCsv,
  controls,
}: {
  authHeaders: AuthHeaders;
  base: string;
  range: DateRange;
  dimensions: InsightDimension[];
  filters: ActiveFilter[];
  onDrill: (f: ActiveFilter) => void;
  /** null on personal pages, where the only person is you. */
  peopleLabel?: string | null;
  title?: string;
  /** Drop the title block; the caller renders its own header. */
  bare?: boolean;
  initialDim?: InsightDimension;
  onCsv?: (dim: InsightDimension) => Promise<void>;
  controls?: React.ReactNode;
}) {
  const entity = useEntity();
  const [dim, setDimState] = React.useState<InsightDimension>(() => {
    const saved = getHashParam("by") as InsightDimension | null;
    if (saved && dimensions.includes(saved)) return saved;
    return initialDim && dimensions.includes(initialDim)
      ? initialDim
      : dimensions[0];
  });
  const setDim = (d: InsightDimension) => {
    setDimState(d);
    setHashParam("by", d === dimensions[0] ? null : d);
  };
  React.useEffect(() => {
    if (dim !== dimensions[0] && !getHashParam("by")) setHashParam("by", dim);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [metric, setMetric] = React.useState<SeriesMetric>("cost_usd");
  const [kind, setKind] = React.useState<"bars" | "line">("bars");
  const flt = filtersOf(filters);
  const prev = previousRange(range);
  const { data, error, loading } = useLoad(
    async (): Promise<[Breakdown, Breakdown]> =>
      Promise.all([
        getBreakdown(authHeaders, base, range, dim, flt, metric),
        getBreakdown(authHeaders, base, prev, dim, flt, metric),
      ]),
    [
      authHeaders,
      base,
      range.start,
      range.end,
      dim,
      metric,
      JSON.stringify(flt),
    ],
  );
  const assign = useStableColors(`${base}|${dim}`);

  if (error)
    return (
      <EmptyState icon="alert" title="Couldn't load spend">
        {error}
      </EmptyState>
    );
  if (loading || !data)
    return (
      <div className="ana-stack" aria-busy>
        <Skeleton height={104} />
        <Skeleton height={320} />
      </div>
    );
  const [now, before] = data;

  const t = now.totals;
  const p = before.totals;
  const spend = toNumber(t.cost_usd);
  const metered = toNumber(t.cost_metered_usd);
  const plan = toNumber(t.cost_api_equivalent_usd);
  const people = humanize(t.active_users);
  const peopleNote = `${people} ${t.active_users === 1 ? "person" : "people"}, ${humanize(t.sessions)} ${t.sessions === 1 ? "session" : "sessions"}`;

  const seriesKeys = Array.from(
    new Set(now.series.flatMap((d) => Object.keys(d.values))),
  );
  const order = [
    ...now.groups.map((g) => g.key ?? "").filter((k) => seriesKeys.includes(k)),
    ...(seriesKeys.includes(OTHER) ? [OTHER] : []),
  ];
  const colorOf = assign(order);
  const nice = (l: string) => (dim === "repo" ? shortRepo(l) : l);
  const labels = new Map(now.groups.map((g) => [g.key ?? "", nice(g.label)]));
  const labelOf = (k: string) => (k === OTHER ? "Other" : (labels.get(k) ?? k));
  const days = now.series.map((d) => ({
    day: d.day,
    values: Object.fromEntries(
      Object.entries(d.values).map(([k, v]) => [k, toNumber(v)]),
    ),
  }));
  const total = valueOf(t, metric);
  const prevBy = new Map(
    before.groups.map((g) => [g.key, valueOf(g.metrics, metric)]),
  );
  const drillKey = DRILL[dim];
  const canDrill = (g: InsightGroup) =>
    !!drillKey &&
    drillId(dim, g.key) != null &&
    !filters.some((f) => f.key === drillKey);
  const dimLabel = DIMENSION_LABELS[dim];
  const mixed = metered >= 0.005 && plan >= 0.005;

  return (
    <div className="ana-stack">
      {!bare && <h2 className="ds-section-title">{title}</h2>}

      <StatStrip>
        <StatTile
          label="Spend at API prices"
          info={`${SPEND_TIP} Changes compare with the same number of days just before.`}
          value={usdShort(t.cost_usd)}
          delta={
            <Delta
              now={toNumber(t.cost_usd)}
              prev={toNumber(p.cost_usd)}
              neutral
            />
          }
          note={peopleLabel ? peopleNote : undefined}
        />
        {mixed && (
          <StatTile
            label="Billed per token"
            info={BILLED_TIP}
            value={usdShort(metered)}
            note={`${pct(metered / spend)} of spend`}
          />
        )}
        {mixed && (
          <StatTile
            label="Covered by a plan"
            info={PLAN_TIP}
            value={usdShort(plan)}
            note={`${pct(plan / spend)} of spend`}
          />
        )}
        <StatTile
          label="Money saved"
          info={MONEY_SAVED_TIP}
          value={usdShort(t.saved_usd)}
          delta={
            <Delta
              now={toNumber(t.saved_usd)}
              prev={toNumber(p.saved_usd)}
              upIsGood
            />
          }
        />
      </StatStrip>
      {offlineNote(now.offline) && (
        <Callout tone="info">{offlineNote(now.offline)}</Callout>
      )}

      <div className="ana-chips">
        <Select
          label="Group by"
          value={dim}
          options={dimensions.map((d) => ({
            value: d,
            label: DIMENSION_LABELS[d],
          }))}
          onChange={setDim}
        />
        <Select
          label="Metric"
          value={metric}
          options={(Object.keys(METRIC_LABEL) as SeriesMetric[])
            .filter((m) => m !== "total_cost_usd")
            .map((m) => ({
              value: m,
              label: METRIC_LABEL[m],
            }))}
          onChange={setMetric}
        />
        <FilterPicker
          authHeaders={authHeaders}
          base={base}
          range={range}
          dimensions={dimensions}
          filters={filters}
          onPick={onDrill}
        />
        {controls}
      </div>

      <Card
        title={`${METRIC_LABEL[metric]} over time, by ${dimLabel.toLowerCase()}`}
        actions={
          <>
            <Segmented
              label="Chart type"
              toggle
              size="sm"
              value={kind}
              options={[
                { value: "bars", label: "Bars" },
                { value: "line", label: "Line" },
              ]}
              onChange={setKind}
            />
          </>
        }
      >
        {total <= 0 ? (
          <EmptyState compact title="Nothing in this range" />
        ) : (
          <>
            <TimeChart
              days={days}
              order={order}
              colorOf={colorOf}
              labelOf={labelOf}
              kind={kind}
              format={(v) => fmt(metric, v)}
            />
            <Legend keys={order} colorOf={colorOf} labelOf={labelOf} />
          </>
        )}
      </Card>

      <Card
        flush
        title={`By ${dimLabel.toLowerCase()}`}
        info={
          dim === "team"
            ? "Click a row for its details. Someone in two teams counts toward both; the totals above count them once."
            : "Click a row for its details."
        }
        actions={onCsv && <CsvButton onClick={() => onCsv(dim)} />}
      >
        {now.groups.length === 0 ? (
          <EmptyState compact title="Nothing to show" />
        ) : (
          <Table label={`By ${dimLabel.toLowerCase()}`} layout="breakdown">
            <thead>
              <tr>
                <th>{dimLabel}</th>
                <th className="num">{METRIC_LABEL[metric]}</th>
                <th className="num hide-sm">Share</th>
                <th className="num hide-lg">Trend</th>
                <th className="num">Change</th>
                {metric !== "requests" && (
                  <th className="num hide-lg">Requests</th>
                )}
                <th className="num hide-sm">
                  <span className="ana-th">
                    Cache hit
                    <InfoDot text={CACHE_HIT_TIP} />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {now.groups.map((g) => {
                const k = g.key ?? "";
                const v = valueOf(g.metrics, metric);
                const entityKind =
                  dim !== "none" && dim !== "cost_basis" ? dim : null;
                const id = drillId(dim, g.key);
                const drill =
                  canDrill(g) || (!!entity && !!entityKind && id != null);
                const share = total ? v / total : null;
                return (
                  <tr
                    key={k || "none"}
                    className={drill ? "click" : undefined}
                    title={
                      drill
                        ? entity
                          ? `Details for ${g.label}`
                          : `Filter to ${g.label}`
                        : undefined
                    }
                    tabIndex={drill ? 0 : undefined}
                    onKeyDown={(e) =>
                      drill &&
                      e.key === "Enter" &&
                      (e.currentTarget as HTMLElement).click()
                    }
                    onClick={
                      drill
                        ? () =>
                            entity && entityKind
                              ? entity.open({
                                  kind: entityKind,
                                  id: id as string,
                                  label: g.label,
                                })
                              : drillKey &&
                                onDrill({
                                  key: drillKey,
                                  value: id as string,
                                  label: `${dimLabel}: ${g.label}`,
                                })
                        : undefined
                    }
                  >
                    <td className="strong">
                      <span className="ana-name">
                        <span
                          className="ana-dot"
                          style={{ background: colorOf(k) }}
                        />
                        <span className="ana-ellipsis" title={g.label}>
                          {nice(g.label)}
                        </span>
                      </span>
                    </td>
                    <td className="num strong">{fmt(metric, v)}</td>
                    <td className="num hide-sm">
                      <span className="ana-share">
                        <span className="ana-share-track" aria-hidden>
                          <span
                            style={{
                              width: `${Math.min(100, (share ?? 0) * 100)}%`,
                              background: colorOf(k),
                            }}
                          />
                        </span>
                        <span>{share == null ? "—" : pct(share)}</span>
                      </span>
                    </td>
                    <td className="ana-trend hide-lg">
                      <Sparkline
                        values={days.map((d) => d.values[k] ?? 0)}
                        color={colorOf(k)}
                      />
                    </td>
                    <td className="num ana-change">
                      <Delta
                        now={v}
                        prev={prevBy.get(g.key) ?? null}
                        upIsGood={metric === "saved_usd"}
                        neutral={metric !== "saved_usd" && metric !== "errors"}
                      />
                    </td>
                    {metric !== "requests" && (
                      <td className="num hide-lg">
                        {humanize(g.metrics.requests)}
                      </td>
                    )}
                    <td className="num hide-sm">
                      {pct(g.metrics.cache_hit_ratio)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
